import React, { useCallback, useEffect, useRef, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import { Button, Modal, Form } from "react-bootstrap";
import Select from "react-select";
import { RiAddFill, RiDeleteBin6Line } from "react-icons/ri";
import { Roller } from "react-awesome-spinners";
import Sidebar from "../Components/Sidebar/Sidebar";
import pagination from "../Components/DataTable/pagination";
import { useConfirmModal } from "../hooks/useConfirmModal";
import CommonConfirmModal from "../Components/Modal/CommonConfirmModal";
import { getactiveFSR, getinactiveFSR, addTechnician, edittechnician } from "../Api/technician";
import { userslist, getdata } from "../Api/bookingApi";
import { canAccessFsrModule } from "../Api/auth";
import { notify } from "../utils/notify";
import "./Fsr.css";
import {
  NotificationContainer,
} from "react-notifications";

function Fsr() {
  const confirmModal = useConfirmModal();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fsrList, setFsrList] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchkeyword, setSearchkeyword] = useState("");
  const pageRef = useRef(1);
  const usersRef = useRef([]);
  const hasFsrAccess = canAccessFsrModule();

  useEffect(() => {
    if (!hasFsrAccess) {
      window.location.href = "/dashboard";
    }
  }, [hasFsrAccess]);

  const isFsrRole = (role) =>
    String(role || "")
      .trim()
      .toLowerCase() === "fsr";

  const isActiveTech = (item) => {
    const status = item?.tech_status;
    return status === 0 || status === "0" || status === null || status === undefined || status === "";
  };

  const getTechName = (item) => item?.fullname || "-";

  const getTechEmail = (item) => item?.email || item?.user_email || "-";

  const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

  const fetchFsrList = useCallback(() => {
    if (!canAccessFsrModule()) return;
    setLoading(true);
    getactiveFSR()
      .then((res) => {
        if (res.code === 200) {
          const apiResponse = res.data;
          const rawList =
            apiResponse?.data?.activefsrlist ||
            apiResponse?.data ||
            [];
          const list = Array.isArray(rawList) ? rawList : [];
          const filtered = list.filter(
            (item) => isFsrRole(item.role) && isActiveTech(item)
          );
          setFsrList(filtered);
        } else {
          setFsrList([]);
          notify.error("Something went wrong");
        }
      })
      .catch(() => {
        setFsrList([]);
        notify.error("Something went wrong");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const loadUsers = useCallback((keyword = "", type = "search") => {
    const page = type === "search" ? 1 : pageRef.current;
    userslist(keyword, page)
      .then((res) => {
        if (res.code === 200) {
          const apiResponse = res.data;
          const list = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
          if (list.length > 0) {
            if (type === "scroll") {
              const next = [...usersRef.current, ...list];
              usersRef.current = next;
              setUsers(next);
              pageRef.current = page + 1;
            } else {
              usersRef.current = list;
              setUsers(list);
              pageRef.current = 2;
            }
          } else if (type === "search") {
            usersRef.current = [];
            setUsers([]);
            pageRef.current = 1;
          }
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    if (!hasFsrAccess) return;
    fetchFsrList();
  }, [fetchFsrList, hasFsrAccess]);

  useEffect(() => {
    if (!hasFsrAccess || !showAddModal) return;
    const timer = setTimeout(() => {
      loadUsers(searchkeyword, "search");
    }, 300);
    return () => clearTimeout(timer);
  }, [showAddModal, searchkeyword, loadUsers, hasFsrAccess]);

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setSelectedUser(null);
    setSearchkeyword("");
    usersRef.current = [];
    setUsers([]);
    pageRef.current = 1;
  };

  const handleShowAddModal = () => {
    setShowAddModal(true);
    setSelectedUser(null);
    setSearchkeyword("");
    pageRef.current = 1;
  };

  const handleInputChange = (value) => {
    setSearchkeyword(String(value || "").trim());
  };

  const isAlreadyActiveFsr = (email) => {
    const target = normalizeEmail(email);
    if (!target) return false;
    return fsrList.some((item) => normalizeEmail(getTechEmail(item)) === target);
  };

  const isInactiveTech = (item) => {
    const status = item?.tech_status;
    return status === 1 || status === "1";
  };

  const buildEditTechnicianPayload = (row, techStatus) => {
    const formData = new FormData();
    formData.append("technician_id", row.id);
    formData.append("name", getTechName(row));
    formData.append("email", getTechEmail(row) === "-" ? "" : getTechEmail(row));
    formData.append("phone_number", row.phone || row.phone_number || "");
    formData.append("keap_id", row.keap_id || "");
    formData.append("address", row.address || "");
    formData.append("city", row.city || "");
    formData.append("state", row.state || "");
    formData.append("country", row.country || "");
    formData.append("description", row.description || "");
    formData.append("my_zip", row.my_zip || row.zip || "");
    formData.append("zipcode", row.zip || row.zipcode || "");
    formData.append("tags", row.tags || "");
    formData.append("working_zone", row.working_zone || "");
    formData.append("service_ids", row.service_ids || "");
    formData.append("timesheet_option_for_teach", row.timesheet_option_for_teach || "N");
    formData.append("enable_booking", row.enable_booking || "N");
    formData.append("enable_availabilty", row.enable_availabilty || "N");
    formData.append("tech_status", techStatus);
    formData.append("role", "FSR");
    if (row.gender) {
      formData.append("gender", row.gender);
    }
    return formData;
  };

  const findInactiveFsrByEmail = async (email) => {
    const target = normalizeEmail(email);
    if (!target) return null;

    const res = await getinactiveFSR();
    if (res.code !== 200) return null;

    const rawList =
      res.data?.data?.inactivefsrlist ||
      res.data?.data ||
      [];
    const list = Array.isArray(rawList) ? rawList : [];

    return (
      list.find(
        (item) =>
          isFsrRole(item.role) &&
          isInactiveTech(item) &&
          normalizeEmail(getTechEmail(item)) === target
      ) || null
    );
  };

  const reactivateFsr = async (row) => {
    const res = await edittechnician(buildEditTechnicianPayload(row, 0));
    if (res.code === 200) {
      notify.success("FSR reactivated successfully");
      handleCloseAddModal();
      fetchFsrList();
      return true;
    }
    notify.error("Something went wrong");
    return false;
  };

  const handleAddFsr = async () => {
    if (!selectedUser) {
      notify.error("Please select a user");
      return;
    }

    setSubmitting(true);
    try {
      let name = selectedUser.label || "";
      let email = selectedUser.email || selectedUser.user_email || "";
      let keapId = selectedUser.keap_id || "";

      if (selectedUser.value) {
        const formData = new FormData();
        formData.append("user_id", selectedUser.value);
        const res = await getdata(formData);
        if (res.code === 200 && res.data?.status === "success" && res.data?.data) {
          const user = res.data.data;
          name =
            [user.firstname, user.lastname].filter(Boolean).join(" ").trim() ||
            name;
          email = user.email || user.user_email || email;
          keapId = user.keap_id || keapId;
        }
      }

      if (!name || !email) {
        notify.error("Selected user is missing name or email");
        return;
      }

      if (isAlreadyActiveFsr(email)) {
        notify.error("FSR Already exist");
        return;
      }

      const inactiveFsr = await findInactiveFsrByEmail(email);
      if (inactiveFsr) {
        await reactivateFsr(inactiveFsr);
        return;
      }

      const payload = new FormData();
      payload.append("name", name);
      payload.append("email", email);
      payload.append("role", "FSR");
      payload.append("keap_id", keapId || "");
      payload.append("password", `Fsr@${name}`);
      payload.append("image", "");

      const addRes = await addTechnician(payload);
      if (addRes.code === 200) {
        if (addRes.data?.status === "success") {
          notify.success("FSR added successfully");
          handleCloseAddModal();
          fetchFsrList();
        } else {
          const message = (addRes.data?.message || "Something went wrong")
            .toString()
            .replace(/<\/?p>/gi, "")
            .trim();
          const lowerMsg = message.toLowerCase();
          if (
            lowerMsg.includes("already") ||
            lowerMsg.includes("exist") ||
            lowerMsg.includes("duplicate")
          ) {
            const existingInactive = await findInactiveFsrByEmail(email);
            if (existingInactive) {
              await reactivateFsr(existingInactive);
              return;
            }
            notify.error("Already exist");
          } else {
            notify.error(message || "Something went wrong");
          }
        }
      } else {
        notify.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      notify.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    const confirmed = await confirmModal.askConfirmation(
      "Are you sure you want to remove this FSR?"
    );
    if (!confirmed) return;

    setSubmitting(true);
    edittechnician(buildEditTechnicianPayload(row, 1))
      .then((res) => {
        if (res.code === 200) {
          notify.success("FSR removed successfully");
          fetchFsrList();
        } else {
          notify.error("Something went wrong");
        }
      })
      .catch(() => {
        notify.error("Something went wrong");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const actionFormatter = (cell, row) => (
    <div className="button-group">
      <Button
        className="Delete-Active-bttn me-2"
        onClick={() => handleDelete(row)}
        disabled={submitting}
      >
        <RiDeleteBin6Line />
      </Button>
    </div>
  );

  const columns = [
    {
      dataField: "Name",
      text: "Name",
      sort: true,
      formatter: (cell, row) => getTechName(row),
    },
    {
      dataField: "email",
      text: "Email",
      sort: true,
      formatter: (cell, row) => getTechEmail(row),
    },
    {
      dataField: "action",
      text: "Action",
      formatter: actionFormatter,
      headerStyle: { width: "160px" },
    },
  ];

  if (!hasFsrAccess) {
    return null;
  }

  return (
    <div>
      <CommonConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onClose={confirmModal.onClose}
      />

      <Modal show={showAddModal} onHide={handleCloseAddModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add FSR</Modal.Title>
        </Modal.Header>
        <Modal.Body className="custum-modal-body">
          <Form.Group className="mb-3">
            <Form.Label>Select User</Form.Label>
            <Select
              value={selectedUser}
              options={users}
              onChange={(option) => setSelectedUser(option)}
              onInputChange={(value, { action }) => {
                if (action === "input-change") {
                  handleInputChange(value);
                }
              }}
              onMenuScrollToBottom={() => loadUsers(searchkeyword, "scroll")}
              placeholder="Search and select user"
              isClearable
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <button className="Cancel-bttn btn" type="button" onClick={handleCloseAddModal}>
            Cancel
          </button>
          <button
            className="Active-bttn btn"
            type="button"
            onClick={handleAddFsr}
            disabled={submitting}
          >
            {submitting ? "Adding..." : "Add User"}
          </button>
        </Modal.Footer>
      </Modal>

      {loading ? (
        <div className="loading">
          <Roller size={50} color="#75b317" />
        </div>
      ) : (
        <div className="dashboard-container d-flex">
          <div className="sidebar-container">
            <Sidebar />
          </div>
          <div className="main-container">
            <div className="main-page-body-content">
              <div className="main-page-body-content-body">
                <div className="main-page-body-content-body-container">
                  <div className="container-fulid">
                    <div className="table-card">
                      <div className="card-body table-card-body">
                        <div className="table-card-header mb-4 d-flex justify-content-between align-items-center">
                          <h5 className="card-title mb-0">FSR</h5>
                          <button
                            className="btn btn-primary d-flex align-items-center btn-sm add-user-btn"
                            onClick={handleShowAddModal}
                            type="button"
                          >
                            <RiAddFill /> ADD FSR
                          </button>
                        </div>
                        <div className="table-card-body-inner">
                          <div className="animated fadeIn">
                            <div className="row">
                              <div className="col-md-12">
                                <div className="card">
                                  <div className="card-body">
                                    <BootstrapTable
                                      bootstrap4
                                      keyField="id"
                                      data={fsrList}
                                      columns={columns}
                                      pagination={pagination}
                                      noDataIndication="No FSR found"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <NotificationContainer />
    </div>
  );
}

export default Fsr;
